import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import {
  CATEGORY_DISPLAY_ORDER,
  describeBand,
  trustScoreService,
  type TrustScoreApiError,
  type TrustScoreReadResponse,
  type TrustScoreSignalsResponse,
} from '../services/trustScoreService';
import { SignalsTable } from './SignalsTable';

// FEAT-043-admin-web (MVP-8.9, MVP-18.24).
//
// Hard privacy rules baked into this panel (asserted in
// `admin-web/e2e/trust-score-panel.spec.ts`):
//
//   1. The panel does NOT pre-load score data on page mount. It is
//      only mounted by `TrustScoreSection` after the read-reason
//      dialog is submitted, and it immediately issues the audited
//      `requestScoreRead` / `requestSignalsRead` calls.
//   2. The panel does NOT cache the score in localStorage or
//      sessionStorage. The data lives only in component state for the
//      lifetime of this mount. Closing the user-detail drawer unmounts
//      this component and the data is gone; a fresh open requires a
//      fresh read-reason.
//   3. On a 403 from any endpoint the panel renders a neutral
//      permission-denied message and NEVER includes score data in the
//      rejection screen.
//   4. There are no export / share affordances — no "download CSV",
//      no "copy link". The data lives only in this admin session view.

export interface TrustScorePanelProps {
  userId: string;
  reason: string;
}

interface PanelState {
  scoreLoading: boolean;
  scoreError: TrustScoreApiError | null;
  scoreData: TrustScoreReadResponse | null;
  signalsLoading: boolean;
  signalsError: TrustScoreApiError | null;
  signalsData: TrustScoreSignalsResponse | null;
  recomputing: boolean;
  recomputeError: TrustScoreApiError | null;
}

const initialState: PanelState = {
  scoreLoading: true,
  scoreError: null,
  scoreData: null,
  signalsLoading: true,
  signalsError: null,
  signalsData: null,
  recomputing: false,
  recomputeError: null,
};

function isForbidden(error: TrustScoreApiError | null): boolean {
  return Boolean(error && error.status === 403);
}

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function thirtyDayAxisDates(): string[] {
  const now = new Date();
  const dates: string[] = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    dates.push(d.toISOString().slice(0, 10));
  }
  return dates;
}

interface TrendChartProps {
  currentScore: number | null;
  lastComputedAt: string | null;
}

// Minimal inline SVG trend chart. The api does NOT yet have a daily
// snapshot endpoint — only the current TrustScore row exists. We render
// a 30-day axis with a single data point (today's score) and a
// "(historical trend will appear as data accrues)" hint. We do NOT
// fabricate trend data — the future endpoint shape is documented in
// `docs/features/FEAT-043-Implementation.md`.
const TrendChart: React.FC<TrendChartProps> = ({ currentScore, lastComputedAt }) => {
  const dates = React.useMemo(thirtyDayAxisDates, []);
  const width = 480;
  const height = 140;
  const padX = 24;
  const padY = 16;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;
  const xForIndex = (i: number) => padX + (innerW * i) / Math.max(dates.length - 1, 1);
  const yForScore = (score: number) => padY + innerH * (1 - score / 100);

  const todayIndex = dates.length - 1;
  const pointDate = lastComputedAt ? lastComputedAt.slice(0, 10) : todayIsoDate();
  const pointIndex = Math.max(dates.indexOf(pointDate), todayIndex);
  const showPoint = currentScore != null;

  return (
    <Box data-testid="trust-score-trend-chart">
      <Typography variant="subtitle2" gutterBottom>
        30-day trend
      </Typography>
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <svg
          role="img"
          aria-label="Composite trust score over the last 30 days"
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          height={height}
          style={{ display: 'block' }}
        >
          <rect
            x={padX}
            y={padY}
            width={innerW}
            height={innerH}
            fill="rgba(0,0,0,0.03)"
            stroke="rgba(0,0,0,0.12)"
          />
          {[0, 25, 50, 75, 100].map((tick) => (
            <line
              key={tick}
              x1={padX}
              x2={padX + innerW}
              y1={yForScore(tick)}
              y2={yForScore(tick)}
              stroke="rgba(0,0,0,0.06)"
              strokeDasharray="2 4"
            />
          ))}
          {[0, 25, 50, 75, 100].map((tick) => (
            <text
              key={`label-${tick}`}
              x={4}
              y={yForScore(tick) + 4}
              fontSize="10"
              fill="rgba(0,0,0,0.55)"
            >
              {tick}
            </text>
          ))}
          {showPoint && currentScore != null ? (
            <g>
              <circle
                cx={xForIndex(pointIndex)}
                cy={yForScore(currentScore)}
                r={5}
                fill="#1976d2"
              />
              <text
                x={xForIndex(pointIndex) - 4}
                y={yForScore(currentScore) - 10}
                fontSize="10"
                fill="rgba(0,0,0,0.7)"
              >
                {currentScore}
              </text>
            </g>
          ) : null}
          <text x={padX} y={height - 2} fontSize="10" fill="rgba(0,0,0,0.55)">
            {dates[0]}
          </text>
          <text
            x={padX + innerW - 60}
            y={height - 2}
            fontSize="10"
            fill="rgba(0,0,0,0.55)"
          >
            {dates[dates.length - 1]}
          </text>
        </svg>
      </Box>
      <Typography variant="caption" color="text.secondary">
        Historical trend will appear as data accrues — only the current point is
        shown today (no fabricated history).
      </Typography>
    </Box>
  );
};

export const TrustScorePanel: React.FC<TrustScorePanelProps> = ({ userId, reason }) => {
  const [state, setState] = React.useState<PanelState>(initialState);
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  const loadScore = React.useCallback(async () => {
    setState((prev) => ({ ...prev, scoreLoading: true, scoreError: null }));
    try {
      const result = await trustScoreService.requestScoreRead({ userId, reason });
      setState((prev) => ({
        ...prev,
        scoreLoading: false,
        scoreError: null,
        scoreData: result,
      }));
    } catch (err) {
      const apiError = err as TrustScoreApiError;
      setState((prev) => ({
        ...prev,
        scoreLoading: false,
        scoreError: apiError,
        scoreData: null,
      }));
    }
  }, [userId, reason]);

  const loadSignals = React.useCallback(
    async (nextPage: number, nextLimit: number) => {
      setState((prev) => ({ ...prev, signalsLoading: true, signalsError: null }));
      try {
        const result = await trustScoreService.requestSignalsRead({
          userId,
          reason,
          page: nextPage,
          limit: nextLimit,
        });
        setState((prev) => ({
          ...prev,
          signalsLoading: false,
          signalsError: null,
          signalsData: result,
        }));
      } catch (err) {
        const apiError = err as TrustScoreApiError;
        setState((prev) => ({
          ...prev,
          signalsLoading: false,
          signalsError: apiError,
          signalsData: null,
        }));
      }
    },
    [userId, reason],
  );

  React.useEffect(() => {
    void loadScore();
  }, [loadScore]);

  React.useEffect(() => {
    void loadSignals(page, rowsPerPage);
  }, [loadSignals, page, rowsPerPage]);

  const handleRecompute = async () => {
    setState((prev) => ({ ...prev, recomputing: true, recomputeError: null }));
    try {
      await trustScoreService.requestRecompute({ userId });
      await Promise.all([loadScore(), loadSignals(page, rowsPerPage)]);
      setState((prev) => ({ ...prev, recomputing: false }));
    } catch (err) {
      setState((prev) => ({
        ...prev,
        recomputing: false,
        recomputeError: err as TrustScoreApiError,
      }));
    }
  };

  // Neutral denial — NEVER include score data, even if a prior render
  // had it. Re-render with this when ANY admin call returns 403.
  if (isForbidden(state.scoreError) || isForbidden(state.signalsError)) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 3 }}
        data-testid="trust-score-permission-denied"
      >
        <Alert severity="info" icon={false}>
          You don&rsquo;t have permission to view this.
        </Alert>
      </Paper>
    );
  }

  const score = state.scoreData?.score ?? null;
  const composite = score?.compositeScore ?? null;
  const band = describeBand(composite);

  return (
    <Paper variant="outlined" sx={{ p: 3 }} data-testid="trust-score-panel">
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Stack spacing={1} alignItems="flex-start">
            <Typography variant="overline" color="text.secondary">
              Composite Trust Score
            </Typography>
            <Stack direction="row" spacing={2} alignItems="baseline">
              <Typography
                variant="h3"
                component="div"
                data-testid="trust-score-composite-value"
              >
                {composite ?? '—'}
                <Typography component="span" variant="h6" color="text.secondary">
                  {composite != null ? ' / 100' : ''}
                </Typography>
              </Typography>
              <Chip
                color={band.color}
                label={band.label}
                size="small"
                data-testid="trust-score-band"
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {band.description}
            </Typography>
            {score ? (
              <Typography variant="caption" color="text.secondary">
                Based on {score.signalCount} signal{score.signalCount === 1 ? '' : 's'} ·
                last computed {new Date(score.lastComputedAt).toLocaleString()}
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary">
                No score row exists yet for this user.
              </Typography>
            )}
          </Stack>
          <Stack spacing={1} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
            <Tooltip title="Recompute the score from the last 90 days of signals. The reason captured when you opened this panel covers this action.">
              <span>
                <Button
                  variant="outlined"
                  startIcon={
                    state.recomputing ? <CircularProgress size={16} /> : <RefreshIcon />
                  }
                  onClick={() => void handleRecompute()}
                  disabled={state.recomputing}
                  data-testid="trust-score-recompute"
                >
                  {state.recomputing ? 'Recomputing…' : 'Recompute'}
                </Button>
              </span>
            </Tooltip>
            {state.recomputeError ? (
              <Typography variant="caption" color="error">
                {state.recomputeError.message}
              </Typography>
            ) : null}
          </Stack>
        </Stack>

        {state.scoreLoading ? <LinearProgress /> : null}

        {state.scoreError && !isForbidden(state.scoreError) ? (
          <Alert severity="error">
            Could not load trust score: {state.scoreError.message}
          </Alert>
        ) : null}

        <Divider />

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Category breakdown
          </Typography>
          <Stack spacing={1.5}>
            {CATEGORY_DISPLAY_ORDER.map(({ key, label, hint }) => {
              const value = score?.categoryScores?.[key] ?? null;
              const display = value == null ? null : Math.max(0, Math.min(100, value));
              return (
                <Box
                  key={key}
                  data-testid={`trust-score-category-${key}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '180px 1fr 80px',
                    gap: 2,
                    alignItems: 'center',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="baseline">
                    <Typography variant="body2">{label}</Typography>
                    {hint ? (
                      <Typography variant="caption" color="text.secondary">
                        ({hint})
                      </Typography>
                    ) : null}
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={display ?? 0}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: 'rgba(0,0,0,0.06)',
                      opacity: display == null ? 0.4 : 1,
                    }}
                  />
                  <Typography
                    variant="body2"
                    color={display == null ? 'text.secondary' : 'text.primary'}
                    align="right"
                  >
                    {display == null ? 'Not enough signal' : `${display}`}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>

        <Divider />

        <TrendChart
          currentScore={composite}
          lastComputedAt={score?.lastComputedAt ?? null}
        />

        <Divider />

        <Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="baseline"
            justifyContent="space-between"
          >
            <Typography variant="subtitle2">Raw signals</Typography>
            <Typography variant="caption" color="text.secondary">
              Audited reads — no export.
            </Typography>
          </Stack>
          {state.signalsError && !isForbidden(state.signalsError) ? (
            <Alert severity="error" sx={{ mb: 1 }}>
              Could not load signals: {state.signalsError.message}
            </Alert>
          ) : null}
          <SignalsTable
            loading={state.signalsLoading}
            data={state.signalsData}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={(next) => {
              setRowsPerPage(next);
              setPage(1);
            }}
          />
        </Box>
      </Stack>
    </Paper>
  );
};

export default TrustScorePanel;
